import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, Button, Input } from '../components/ui'
import { PageHeader } from '../../ui'
import { useIXVerifyAuth } from '../contexts'
import { supabase } from '../../services/supabase'
import { getSite, listSavedRecipients, createSavedRecipient, updateSavedRecipient, deleteSavedRecipient, updateProfileName } from '../lib/ixverifyApi'
import { validatePassword, getPasswordRequirements, getStrengthColor, getStrengthWidth } from '../lib/passwordPolicy'
import { logPasswordChanged } from '../lib/auditLog'
import { APP_VERSION, BUILD_DATE } from '../../config/version'
import type { IXVerifyRole, Site, SavedRecipient } from '../types'

// Alias for UserRole
type UserRole = IXVerifyRole;

export function Settings() {
  const { user, supabaseUser, signOut, loading: authLoading } = useIXVerifyAuth()
  const [site, setSite] = useState<Site | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')

  // Form state
  const [fullName, setFullName] = useState('')
  const [notifications, setNotifications] = useState(true)

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [_currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)

  // Saved recipients state
  const [recipients, setRecipients] = useState<SavedRecipient[]>([])
  const [recipientsLoading, setRecipientsLoading] = useState(false)
  const [showAddRecipient, setShowAddRecipient] = useState(false)
  const [editingRecipientId, setEditingRecipientId] = useState<string | null>(null)
  const [recipientEmail, setRecipientEmail] = useState('')
  const [recipientLabel, setRecipientLabel] = useState('')
  const [recipientError, setRecipientError] = useState('')
  const [savingRecipient, setSavingRecipient] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return

    if (user) {
      setFullName(user.full_name || '')
      if (user.site_id) {
        fetchSite(user.site_id)
      } else {
        setLoading(false)
      }
    } else if (supabaseUser) {
      setFullName(supabaseUser.user_metadata?.full_name || '')
      setLoading(false)
    } else {
      setLoading(false)
    }
  }, [user?.id, user?.full_name, user?.site_id, supabaseUser?.id, authLoading])

  const fetchSite = async (siteId: string) => {
    try {
      const { data, error: fetchError } = await getSite(siteId)

      if (fetchError) throw fetchError
      setSite(data)
    } catch (err) {
      console.error('Error fetching site:', err)
    } finally {
      setLoading(false)
    }
  }

  // Load saved recipients
  useEffect(() => {
    if (user?.site_id) {
      fetchRecipients()
    }
  }, [user?.site_id])

  const fetchRecipients = async () => {
    if (!user?.site_id) return
    setRecipientsLoading(true)
    try {
      const { data, error } = await listSavedRecipients(user.site_id)

      if (error) throw error
      setRecipients(data || [])
    } catch (error) {
      console.error('Error fetching recipients:', error)
    } finally {
      setRecipientsLoading(false)
    }
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const resetRecipientForm = () => {
    setRecipientEmail('')
    setRecipientLabel('')
    setRecipientError('')
    setShowAddRecipient(false)
    setEditingRecipientId(null)
  }

  const handleAddRecipient = async () => {
    if (!user?.site_id) return

    if (!recipientEmail.trim()) {
      setRecipientError('Email is required')
      return
    }
    if (!validateEmail(recipientEmail.trim())) {
      setRecipientError('Please enter a valid email address')
      return
    }
    if (recipients.find(r => r.email.toLowerCase() === recipientEmail.trim().toLowerCase())) {
      setRecipientError('This email already exists')
      return
    }

    setSavingRecipient(true)
    setRecipientError('')

    try {
      const { data, error } = await createSavedRecipient({
        site_id: user.site_id,
        email: recipientEmail.trim(),
        label: recipientLabel.trim() || null,
      })

      if (error) throw error
      setRecipients([...recipients, data])
      resetRecipientForm()
    } catch (error) {
      console.error('Error adding recipient:', error)
      setRecipientError('Failed to add recipient')
    } finally {
      setSavingRecipient(false)
    }
  }

  const handleUpdateRecipient = async (id: string) => {
    if (!recipientEmail.trim()) {
      setRecipientError('Email is required')
      return
    }
    if (!validateEmail(recipientEmail.trim())) {
      setRecipientError('Please enter a valid email address')
      return
    }
    if (recipients.find(r => r.id !== id && r.email.toLowerCase() === recipientEmail.trim().toLowerCase())) {
      setRecipientError('This email already exists')
      return
    }

    setSavingRecipient(true)
    setRecipientError('')

    try {
      const { error } = await updateSavedRecipient(id, {
        email: recipientEmail.trim(),
        label: recipientLabel.trim() || null,
      })

      if (error) throw error
      setRecipients(recipients.map(r => r.id === id ? { ...r, email: recipientEmail.trim(), label: recipientLabel.trim() || null } : r))
      resetRecipientForm()
    } catch (error) {
      console.error('Error updating recipient:', error)
      setRecipientError('Failed to update recipient')
    } finally {
      setSavingRecipient(false)
    }
  }

  const handleDeleteRecipient = async (id: string) => {
    try {
      const { error } = await deleteSavedRecipient(id)

      if (error) throw error
      setRecipients(recipients.filter(r => r.id !== id))
      setDeleteConfirmId(null)
    } catch (error) {
      console.error('Error deleting recipient:', error)
    }
  }

  const startEditingRecipient = (recipient: SavedRecipient) => {
    setEditingRecipientId(recipient.id)
    setRecipientEmail(recipient.email)
    setRecipientLabel(recipient.label || '')
    setRecipientError('')
    setShowAddRecipient(false)
  }

  const handleSaveProfile = async () => {
    if (!user) return

    setSaving(true)
    setError('')
    setSuccess(false)

    try {
      const { error: updateError } = await updateProfileName(fullName.trim())

      if (updateError) throw updateError

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving profile:', err)
      setError('Failed to save profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async () => {
    setPasswordError('')
    setPasswordSuccess(false)

    // Validation
    if (!newPassword) {
      setPasswordError('New password is required')
      return
    }

    // Validate password against CFR 21 Part 11 requirements
    const passwordValidation = validatePassword(newPassword, {
      email: user?.email || supabaseUser?.email,
      fullName
    })
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.errors[0])
      return
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match')
      return
    }

    setChangingPassword(true)

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (updateError) throw updateError

      // Log the password change
      if (user) {
        await logPasswordChanged(user.id, user.site_id || null)
      }

      setPasswordSuccess(true)
      setShowPasswordForm(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setTimeout(() => setPasswordSuccess(false), 3000)
    } catch (err: any) {
      console.error('Error changing password:', err)
      setPasswordError(err.message || 'Failed to change password. Please try again.')
    } finally {
      setChangingPassword(false)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Error signing out:', err)
    }
  }

  // Role colors are a domain color map — hues kept, dark pairs added
  const roleColors: Record<UserRole, string> = {
    superadmin: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
    admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
    coordinator: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
    viewer: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  }

  const roleLabels: Record<UserRole, string> = {
    superadmin: 'Super Admin',
    admin: 'Administrator',
    coordinator: 'Coordinator',
    viewer: 'Viewer',
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <PageHeader title="Settings" subtitle="Manage your account and preferences" />
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <PageHeader title="Settings" subtitle="Manage your account and preferences" />

      {/* Profile Section */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Profile</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-300">
              Full Name
            </label>
            <Input
              type="text"
              placeholder="Your name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-300">
              Email
            </label>
            <Input
              type="email"
              value={user?.email || supabaseUser?.email || ''}
              disabled
              className="bg-gray-50 dark:bg-gray-900/40"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Contact an administrator to change your email
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-300">
              Role
            </label>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${roleColors[user?.role || 'viewer']}`}>
                {roleLabels[user?.role || 'viewer']}
              </span>
            </div>
          </div>

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          {success && (
            <p className="text-sm text-green-600 dark:text-green-400">Profile saved successfully!</p>
          )}

          <Button onClick={handleSaveProfile} disabled={saving}>
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Saving...
              </>
            ) : (
              'Save Profile'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Site Information */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Site Information</h2>
        </CardHeader>
        <CardContent>
          {site ? (
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Site Name</p>
                <p className="font-medium text-gray-900 dark:text-gray-100">{site.name}</p>
              </div>
              {site.address && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Address</p>
                  <p className="text-gray-900 dark:text-gray-100">{site.address}</p>
                </div>
              )}
              {site.default_email && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Default Email</p>
                  <p className="text-gray-900 dark:text-gray-100">{site.default_email}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <svg className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <p className="text-gray-500 dark:text-gray-400">No site assigned</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Contact an administrator to be assigned to a site
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Security</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showPasswordForm ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">Password</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Change your account password</p>
              </div>
              <Button variant="outline" onClick={() => setShowPasswordForm(true)}>
                Change Password
              </Button>
            </div>
          ) : (
            <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-300">
                  New Password
                </label>
                <Input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                {/* Password Requirements Display */}
                {newPassword && (
                  <div className="mt-3 space-y-2">
                    {/* Strength Bar */}
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${getStrengthColor(validatePassword(newPassword).strength)}`}
                          style={{ width: getStrengthWidth(validatePassword(newPassword).strength) }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                        {validatePassword(newPassword).strength}
                      </span>
                    </div>
                    {/* Requirements List */}
                    <div className="grid grid-cols-1 gap-1">
                      {getPasswordRequirements(newPassword, {
                        email: user?.email || supabaseUser?.email,
                        fullName
                      }).map((req, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs">
                          {req.met ? (
                            <svg className="w-3.5 h-3.5 text-green-500 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                            </svg>
                          )}
                          <span className={req.met ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-300">
                  Confirm New Password
                </label>
                <Input
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              {passwordError && (
                <p className="text-sm text-red-600 dark:text-red-400">{passwordError}</p>
              )}

              <div className="flex gap-3">
                <Button onClick={handleChangePassword} disabled={changingPassword}>
                  {changingPassword ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Changing...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPasswordForm(false)
                    setNewPassword('')
                    setConfirmPassword('')
                    setPasswordError('')
                  }}
                  disabled={changingPassword}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {passwordSuccess && (
            <p className="text-sm text-green-600 dark:text-green-400">Password changed successfully!</p>
          )}
        </CardContent>
      </Card>

      {/* Preferences Section */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Preferences</h2>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Email Notifications</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Receive email alerts for verification reports</p>
            </div>
            <button
              onClick={() => setNotifications(!notifications)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                notifications ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  notifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Saved Recipients Section */}
      {user?.site_id && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Saved Recipients</h2>
              {!showAddRecipient && !editingRecipientId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddRecipient(true)
                    setRecipientEmail('')
                    setRecipientLabel('')
                    setRecipientError('')
                  }}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add
                </Button>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Quick-add email recipients when sending verification reports
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add/Edit Form */}
            {(showAddRecipient || editingRecipientId) && (
              <div className="p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-300">
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    placeholder="email@example.com"
                    value={recipientEmail}
                    onChange={(e) => {
                      setRecipientEmail(e.target.value)
                      setRecipientError('')
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 dark:text-gray-300">
                    Label (optional)
                  </label>
                  <Input
                    type="text"
                    placeholder="e.g., Dr. Smith, Pharmacy Lead"
                    value={recipientLabel}
                    onChange={(e) => setRecipientLabel(e.target.value)}
                  />
                </div>
                {recipientError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{recipientError}</p>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => editingRecipientId ? handleUpdateRecipient(editingRecipientId) : handleAddRecipient()}
                    disabled={savingRecipient}
                  >
                    {savingRecipient ? 'Saving...' : editingRecipientId ? 'Update' : 'Add'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetRecipientForm}
                    disabled={savingRecipient}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Recipients List */}
            {recipientsLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 dark:border-primary-400"></div>
              </div>
            ) : recipients.length === 0 ? (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                <svg className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <p className="text-sm">No saved recipients yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add recipients to quickly select them when sending reports</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {recipients.map((recipient) => (
                  <div key={recipient.id} className="py-3 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{recipient.email}</p>
                          {recipient.label && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{recipient.label}</p>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {deleteConfirmId === recipient.id ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteRecipient(recipient.id)}
                            className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs px-2"
                          >
                            Delete
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDeleteConfirmId(null)}
                            className="text-xs px-2"
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditingRecipient(recipient)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(recipient.id)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* App Info */}
      <Card>
        <CardHeader>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">About</h2>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Application</span>
            <span className="text-gray-900 dark:text-gray-100">IX Verify</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Version</span>
            <span className="text-gray-900 dark:text-gray-100">{APP_VERSION}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Build Date</span>
            <span className="text-gray-900 dark:text-gray-100">{BUILD_DATE}</span>
          </div>
        </CardContent>
      </Card>

      {/* Sign Out */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">Sign Out</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Signed in as {user?.email || supabaseUser?.email}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleSignOut}
              className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
